import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { isApp } from '@/lib/app-bridge.ts';
import {
  openNotificationSettings,
  requestPushPermission,
  setPushEnabled,
  syncPush,
  type PushState,
} from '@/lib/push.ts';
import { useAppSelector } from '@/store/hooks.ts';

/**
 * 마이페이지의 '알림 설정'. **앱에서만** 보인다. (2026-10-08)
 *
 * 광고성 푸시의 수신거부 안내(`앱 > 마이페이지 > 알림 설정`)가 가리키는 자리다.
 * 서버(ModulePush::AD_OPT_OUT)의 문구와 이 위치가 어긋나면 안 된다.
 *
 *   앱 알림 받기   이 기기에서 받을지 (서버의 기기 설정 F_PUSH_YN)
 *   광고성 알림    가입 3단계의 '광고성 정보 수신 동의'를 그대로 쓴다. 바꾸려면 정보 수정으로
 *
 * 출금 계좌 칸과 같은 모양을 빌려 쓴다 — 시안에 없는 화면이라 새 스타일을 만들지 않았다.
 */
export const PushSettings: FunctionalComponent = () => {
  const user = useAppSelector((state) => state.auth.user);
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isApp()) return;
    void syncPush().then(setState);
  }, []);

  // 앱이 아니거나, 푸시 설정이 안 된 앱(google-services.json 없음)이면 그리지 않는다.
  if (!state) return null;

  const on = state.pushYn === 'Y';

  async function toggle() {
    if (busy) return;
    setBusy(true);
    try {
      let notiEnabled = state!.notiEnabled;
      // 켜는데 안드로이드 알림이 꺼져 있으면 먼저 권한을 묻는다.
      if (!on && !notiEnabled) notiEnabled = await requestPushPermission();
      if (await setPushEnabled(!on)) setState({ pushYn: on ? 'N' : 'Y', notiEnabled });
    } finally {
      setBusy(false);
    }
  }

  async function fixPermission() {
    const notiEnabled = await requestPushPermission();
    // '다시 묻지 않음' 으로 거절했으면 대화상자가 안 뜬다. 설정 화면으로 보낸다.
    if (!notiEnabled) openNotificationSettings();
    setState({ ...state!, notiEnabled });
  }

  return (
    <div class="withdraw-account-section">
      <div class="withdraw-account-title">알림 설정</div>

      <div class="withdraw-account-row">
        <span class="withdraw-account-detail">
          <span class="withdraw-account-number">앱 알림 받기</span>
          <span class="withdraw-account-empty">
            {on ? '적립·출금·이벤트 소식을 알려 드려요' : '알림을 받지 않아요'}
          </span>
        </span>
        <button
          type="button"
          class="withdraw-account-register-btn"
          disabled={busy}
          onClick={() => void toggle()}
        >
          {on ? '끄기' : '켜기'}
        </button>
      </div>

      {on && !state.notiEnabled && (
        <div class="withdraw-account-row">
          <span class="withdraw-account-empty">휴대폰 설정에서 이 앱의 알림이 꺼져 있어요.</span>
          <button
            type="button"
            class="withdraw-account-register-btn"
            onClick={() => void fixPermission()}
          >
            설정 열기
          </button>
        </div>
      )}

      {user?.id && (
        <div class="withdraw-account-row">
          <span class="withdraw-account-detail">
            <span class="withdraw-account-number">광고성 알림</span>
            <span class="withdraw-account-empty">
              {user.agreeAd === 'Y'
                ? '새 미션·이벤트 소식을 받아요 (광고성 정보 수신 동의)'
                : '광고성 정보 수신에 동의하지 않았어요'}
            </span>
          </span>
          <button
            type="button"
            class="withdraw-account-register-btn"
            onClick={() => route(`/info?mode=edit&r=${encodeURIComponent('/mypage')}`)}
          >
            변경
          </button>
        </div>
      )}
    </div>
  );
};
